import { useEffect, useState } from 'react';
import { Room } from 'livekit-client';

export type DeviceKind = 'audioinput' | 'videoinput' | 'audiooutput';
export type DeviceLists = Record<DeviceKind, MediaDeviceInfo[]>;

// setSinkId (changer le haut-parleur de sortie) n'est pas supporte par Safari —
// les appelants doivent masquer le selecteur correspondant plutot que de
// laisser un choix qui ne s'applique jamais.
export const SUPPORTS_AUDIO_OUTPUT_SELECTION =
  typeof HTMLMediaElement !== 'undefined' && 'setSinkId' in HTMLMediaElement.prototype;

const EMPTY_DEVICES: DeviceLists = { audioinput: [], videoinput: [], audiooutput: [] };

/**
 * Enumere les peripheriques audio/video via Room.getLocalDevices() (livekit-client),
 * qui wrap navigator.mediaDevices.enumerateDevices(). `requestPermissions=false`
 * par defaut — n'affiche pas de prompt de permission au montage ; les labels
 * restent vides ("Microphone N") tant qu'aucune permission n'a ete accordee
 * ailleurs (toggle Audio/Video, preview camera du lobby, etc.).
 */
export function useDeviceEnumeration(requestPermissions = false): DeviceLists {
  const [devices, setDevices] = useState<DeviceLists>(EMPTY_DEVICES);

  useEffect(() => {
    let cancelled = false;
    const kinds: DeviceKind[] = SUPPORTS_AUDIO_OUTPUT_SELECTION
      ? ['audioinput', 'videoinput', 'audiooutput']
      : ['audioinput', 'videoinput'];
    void Promise.all(
      kinds.map((kind) => Room.getLocalDevices(kind, requestPermissions).catch(() => [] as MediaDeviceInfo[])),
    ).then((results) => {
      if (cancelled) return;
      setDevices((prev) => {
        const next = { ...prev };
        kinds.forEach((kind, i) => { next[kind] = results[i] ?? []; });
        return next;
      });
    });
    return () => { cancelled = true; };
  }, [requestPermissions]);

  return devices;
}

export function deviceOptions(devices: DeviceLists, kind: DeviceKind, fallbackLabel: string) {
  return [
    { value: '', label: 'Par défaut (système)' },
    ...devices[kind].map((d, i) => ({ value: d.deviceId, label: d.label || `${fallbackLabel} ${i + 1}` })),
  ];
}
